import { MessageCircleMore, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import delaboLogo from '@/assets/images/Logo_DELABO_portal.svg';
import { SUPPORT_HOTLINE_NUMBER } from '@/shared/config/support';
import { Button } from '@/shared/ui/button';
import { Separator } from '@/shared/ui/separator';
import type appDe from '@/app/i18n/de.json';

/** Dotted `app` namespace key for one of the "Portal" footer-column links. */
type PortalLinkKey = `portal.footer.${Extract<keyof typeof appDe.portal.footer, `portalLink${string}`>}`;
/** Dotted `app` namespace key for one of the "Service" footer-column links. */
type ServiceLinkKey = `portal.footer.${Extract<keyof typeof appDe.portal.footer, `serviceLink${string}`>}`;

interface FooterLinkDef {
  readonly to: string;
  readonly labelKey: PortalLinkKey | ServiceLinkKey;
}

/**
 * "Portal" and "Service" footer columns (Pencil node `FsGwh`, "Footer Col — Portal"/"Footer Col
 * — Service"). All point to the shared `/coming-soon` route — real, clickable `Link`s, never
 * no-op placeholders (accessibility: a focusable link must do something when activated).
 */
const PORTAL_LINKS: readonly FooterLinkDef[] = [
  { to: '/coming-soon', labelKey: 'portal.footer.portalLinkOverview' },
  { to: '/coming-soon', labelKey: 'portal.footer.portalLinkOrders' },
  { to: '/coming-soon', labelKey: 'portal.footer.portalLinkLogistics' },
  { to: '/coming-soon', labelKey: 'portal.footer.portalLinkAcademy' },
];
const SERVICE_LINKS: readonly FooterLinkDef[] = [
  { to: '/coming-soon', labelKey: 'portal.footer.serviceLinkSupportContact' },
  { to: '/coming-soon', labelKey: 'portal.footer.serviceLinkHelpFaq' },
];

const footerLinkClassName = 'text-sm text-muted-foreground transition-colors hover:text-foreground';

interface FooterColumnProps {
  readonly titleKey: 'portal.footer.portalColumnTitle' | 'portal.footer.serviceColumnTitle';
  readonly links: readonly FooterLinkDef[];
}

/** One titled column of `Link`s ("Portal" or "Service"). */
function FooterColumn({ titleKey, links }: FooterColumnProps) {
  const { t } = useTranslation('app');
  return (
    <div className="flex flex-col gap-2.5">
      <h2 className="text-sm font-bold text-foreground">{t(titleKey)}</h2>
      {links.map(({ to, labelKey }) => (
        <Link key={labelKey} to={to} className={footerLinkClassName}>
          {t(labelKey)}
        </Link>
      ))}
    </div>
  );
}

/**
 * Footer of the portal shell chrome (Pencil node `FsGwh`/`QCthB`, "Footer"): brand column,
 * "Portal"/"Service" link columns, a support-hotline column with a "Feedback geben" action, and
 * a bottom legal bar (copyright, Datenschutz/AGB/Impressum). Organisation-type-agnostic
 * (mandantenneutral) — it carries **no** organisation name and no other per-tenant content, so it
 * works unchanged for DELABO-Labor, externes Labor and Praxis contexts alike (e01-03-ak-5).
 *
 * The legal-bar links navigate to the real placeholder routes registered in Item 08
 * (`/datenschutz`, `/agb`, `/impressum`); the Portal/Service columns point at the shared
 * `/coming-soon` route (no real target yet). The Feedback button is a genuine `mailto:` anchor
 * (not a JS `onClick` handler), so it works without JavaScript and is announced correctly by
 * assistive technology as an email link.
 *
 * This component deliberately has no `sticky`/`fixed` positioning — the non-sticky footer layout
 * is produced by the shell (`AppShell`, Item 13), not by this component.
 */
export function AppFooter() {
  const { t } = useTranslation('app');

  return (
    <footer className="border-t bg-muted" role="contentinfo">
      <div className="mx-auto max-w-[1440px]">
        <div className="grid grid-cols-1 gap-8 px-6 py-8 pb-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-3">
            <img src={delaboLogo} alt={t('portal.header.brand')} className="h-10 w-auto" />
          </div>

          <FooterColumn titleKey="portal.footer.portalColumnTitle" links={PORTAL_LINKS} />
          <FooterColumn titleKey="portal.footer.serviceColumnTitle" links={SERVICE_LINKS} />

          <div className="flex flex-col gap-2.5">
            <h2 className="text-sm font-bold text-foreground">{t('portal.footer.hotlineTitle')}</h2>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-primary" aria-hidden="true" />
              <span className="text-sm font-bold text-foreground">
                {t('portal.footer.hotlineNumber', { hotline: SUPPORT_HOTLINE_NUMBER })}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">{t('portal.footer.hotlineHours')}</span>
            <Button asChild size="sm" className="w-fit gap-1.5">
              <a href="mailto:info@delabo.group">
                <MessageCircleMore className="h-4 w-4" aria-hidden="true" />
                {t('portal.footer.feedbackButton')}
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Full-bleed divider (Pencil node `QEGFe`, "Footer Bottom" top stroke): spans the
          footer's entire width, not just the `max-w-[1440px]` content column — deliberately
          outside that wrapper so it still reaches both edges on viewports wider than 1440px. */}
      <Separator />

      <div className="mx-auto max-w-[1440px]">
        <div className="flex flex-wrap items-center justify-between gap-6 px-6 py-4 text-xs text-muted-foreground">
          <span>
            &copy; {new Date().getFullYear()} {t('portal.footer.copyright')}
          </span>
          <nav aria-label={t('portal.footer.legalNav')} className="flex flex-wrap items-center gap-4">
            <Link to="/datenschutz" className="hover:text-foreground">
              {t('portal.footer.privacy')}
            </Link>
            <Link to="/agb" className="hover:text-foreground">
              {t('portal.footer.terms')}
            </Link>
            <Link to="/impressum" className="hover:text-foreground">
              {t('portal.footer.imprint')}
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
